/**
 * Per-config activity stats derived from a flat list of runs. Used to
 * power "Last fired" and "Most active" sort orders on the Configure list
 * tabs without requiring a backend aggregation endpoint.
 *
 * Generic on the run shape — callers pass a `configIdOf` extractor and a
 * `timestampOf` extractor (typically `created_at`).
 */
export interface ActivityStats {
  fireCount: number
  lastFiredAt: string | null
}

const EMPTY: ActivityStats = { fireCount: 0, lastFiredAt: null }

export function buildActivityMap<T>(
  runs: T[],
  configIdOf: (run: T) => string | null | undefined,
  timestampOf: (run: T) => string | null | undefined
): Map<string, ActivityStats> {
  const out = new Map<string, ActivityStats>()
  for (const run of runs) {
    const id = configIdOf(run)
    if (!id) continue
    const ts = timestampOf(run)
    const prev = out.get(id) ?? EMPTY
    out.set(id, {
      fireCount: prev.fireCount + 1,
      lastFiredAt: ts && (!prev.lastFiredAt || ts > prev.lastFiredAt) ? ts : prev.lastFiredAt
    })
  }
  return out
}

export function activityFor(map: Map<string, ActivityStats>, configId: string): ActivityStats {
  return map.get(configId) ?? EMPTY
}

/** Compare two ISO timestamps for "recent first" ordering, treating null as oldest. */
export function compareRecentFirst(
  a: string | null | undefined,
  b: string | null | undefined
): number {
  if (a === b) return 0
  if (!a) return 1
  if (!b) return -1
  return a > b ? -1 : 1
}
